import {
    chartRefreshScheduler,
    computeStaleState,
    formatAge,
    FrameBudgetTracker,
    measureFrame,
    RingBuffer,
    getStaleThresholdMs,
    setSnapshot,
    getSnapshot,
    reportChartRenderMetric,
} from '../../charts/engine';
import { renderHeatmapGrid, renderLineChart, renderTreemapChart, type TreemapItem } from '../../charts/primitives';
import { fetchIndices } from '../../services/market-data';
import { isFeatureEnabled } from '../../services/runtime-config';

type SectorView = {
    name: string;
    change: number;
    weight: number;
};

type PersistedSectorSnapshot = {
    sectors: SectorView[];
    breadthSeries: number[];
    concentrationSeries: number[];
    lastUpdatedAt: number;
};

const SECTOR_MAP: Record<string, { name: string; weight: number }> = {
    'NIFTY BANK': { name: 'Banking', weight: 30 },
    'NIFTY IT': { name: 'IT', weight: 18 },
    'NIFTY ENERGY': { name: 'Energy', weight: 16 },
    'NIFTY AUTO': { name: 'Auto', weight: 12 },
    'NIFTY FMCG': { name: 'FMCG', weight: 10 },
    'NIFTY METAL': { name: 'Metals', weight: 6 },
    'NIFTY PHARMA': { name: 'Pharma', weight: 5 },
    'NIFTY REALTY': { name: 'Realty', weight: 3 },
    'NIFTY FINANCIAL SERVICES': { name: 'Financials', weight: 25 },
    'NIFTY MEDIA': { name: 'Media', weight: 2 },
    'NIFTY PSU BANK': { name: 'PSU Banks', weight: 8 },
    'NIFTY PRIVATE BANK': { name: 'Pvt Banks', weight: 15 },
};

const teardownMap = new WeakMap<HTMLElement, () => void>();

export function renderPhase2SectorsEngine(container: HTMLElement): void {
    if (!isFeatureEnabled('charts_v2_engine')) {
        container.innerHTML = '<div class="wm-empty">charts_v2_engine flag is disabled.</div>';
        return;
    }

    teardownMap.get(container)?.();

    container.innerHTML = `
      <div class="v2-chart-card glass-card">
        <div class="card-header">
          <h3>Phase 2 Engine: Sectors</h3>
          <span class="text-xs text-muted">Tier B refresh</span>
        </div>

        <div class="v2-chart-grid">
          <div class="v2-chart-slot">
            <div class="v2-chart-title">Sector Treemap</div>
            <div id="v2-sector-treemap"></div>
          </div>

          <div class="v2-chart-slot">
                        <div class="v2-chart-title">Concentration Pressure Grid</div>
            <canvas id="v2-sector-heatmap" class="v2-chart-canvas"></canvas>
          </div>

                    <div class="v2-chart-slot">
                        <div class="v2-chart-title">Rotation Breadth</div>
                        <canvas id="v2-sector-breadth" class="v2-chart-canvas"></canvas>
                    </div>

                    <div class="v2-chart-slot">
                        <div class="v2-chart-title">Concentration Pulse</div>
                        <canvas id="v2-sector-concentration" class="v2-chart-canvas"></canvas>
                    </div>

          <div class="v2-chart-slot v2-chart-slot--wide">
            <div class="v2-chart-title">Top Sector Movers</div>
            <div id="v2-sector-board" class="v2-sector-board"></div>
          </div>
        </div>

        <div class="v2-chart-meta">
          <span id="v2-sector-freshness" class="text-xs">Waiting for live sector feed...</span>
          <span id="v2-sector-frame" class="text-xs text-muted" style="margin-left: var(--space-3);">Frame: --</span>
        </div>

        <div id="v2-sector-stale" class="v2-stale-overlay" style="display:none;">
          Sector feed delayed. Rendering cached snapshot.
        </div>
      </div>
    `;

    const treemapNode = container.querySelector('#v2-sector-treemap') as HTMLElement | null;
    const heatmapCanvas = container.querySelector('#v2-sector-heatmap') as HTMLCanvasElement | null;
    const breadthCanvas = container.querySelector('#v2-sector-breadth') as HTMLCanvasElement | null;
    const concentrationCanvas = container.querySelector('#v2-sector-concentration') as HTMLCanvasElement | null;
    const board = container.querySelector('#v2-sector-board') as HTMLElement | null;
    const freshnessNode = container.querySelector('#v2-sector-freshness') as HTMLElement | null;
    const frameNode = container.querySelector('#v2-sector-frame') as HTMLElement | null;
    const staleNode = container.querySelector('#v2-sector-stale') as HTMLElement | null;

    if (!treemapNode || !heatmapCanvas || !breadthCanvas || !concentrationCanvas || !board || !freshnessNode || !frameNode || !staleNode) return;

    const frameTracker = new FrameBudgetTracker(200, 16);
    const staleThresholdMs = getStaleThresholdMs('B');
    const snapshotKey = 'phase2:sectors-engine';
    const breadthSeries = new RingBuffer<number>(180);
    const concentrationSeries = new RingBuffer<number>(180);

    let lastUpdatedAt: number | null = null;
    let lastSectors: SectorView[] = [];

    const getBreadth = (sectors: SectorView[]): number => {
        if (sectors.length === 0) return 0;
        const positive = sectors.filter((s) => s.change >= 0).length;
        return (positive / sectors.length) * 100;
    };

    const getConcentration = (sectors: SectorView[]): number => {
        const weighted = sectors.map((s) => Math.abs(s.change) * Math.max(1, s.weight));
        const total = weighted.reduce((sum, value) => sum + value, 0);
        if (total <= 0) return 0;
        const hhi = weighted.reduce((sum, value) => {
            const share = value / total;
            return sum + (share * share);
        }, 0);
        return hhi * 100;
    };

    const draw = (sectors: SectorView[]) => {
        const sample = measureFrame('sectors-engine', () => {
            const treemapData: TreemapItem[] = sectors.map(s => ({
                label: s.name,
                value: s.weight,
                change: s.change,
            }));
            renderTreemapChart(treemapNode, treemapData);

            renderLineChart(breadthCanvas, breadthSeries.toArray(), '#15b57a');
            renderLineChart(concentrationCanvas, concentrationSeries.toArray(), '#ff9f43');

            const cols = 4;
            const rows = 3;
            const cells = sectors.slice(0, cols * rows).map((sector, idx) => ({
                x: idx % cols,
                y: Math.floor(idx / cols),
                value: Math.max(-5, Math.min(5, (sector.change * (sector.weight / 10)))),
            }));
            renderHeatmapGrid(heatmapCanvas, cells, cols, rows);

            const movers = [...sectors].sort((a, b) => b.change - a.change).slice(0, 6);
            const breadth = getBreadth(sectors);
            const concentration = getConcentration(sectors);
            const breadthTone = breadth >= 60 ? 'terminal-text--up' : breadth <= 40 ? 'terminal-text--down' : '';
            const concentrationTone = concentration >= 28 ? 'terminal-text--down' : concentration <= 16 ? 'terminal-text--up' : '';

            const moverRows = movers.map(item => {
                const tone = item.change >= 0 ? 'terminal-text--up' : 'terminal-text--down';
                return `<div class="terminal-list__row"><span>${item.name}</span><strong class="${tone}">${item.change.toFixed(2)}%</strong></div>`;
            }).join('');

            board.innerHTML = `
              <div class="terminal-list__row"><span>Rotation Breadth</span><strong class="${breadthTone}">${breadth.toFixed(1)}%</strong></div>
              <div class="terminal-list__row"><span>Concentration HHI</span><strong class="${concentrationTone}">${concentration.toFixed(2)}</strong></div>
              ${moverRows}
            `;
        });

        frameTracker.push(sample.label, sample.durationMs);
        const avg = frameTracker.getAverageMs();
        const within = frameTracker.isWithinBudget(sample.durationMs);
        frameNode.textContent = `Frame: ${sample.durationMs.toFixed(2)}ms | avg ${avg.toFixed(2)}ms | budget ${within ? 'OK' : 'OVER'}`;

        reportChartRenderMetric({
            id: 'phase2-sectors',
            label: 'Sectors Engine',
            latestMs: sample.durationMs,
            averageMs: avg,
            budgetMs: frameTracker.getBudgetMs(),
            withinBudget: within,
        });

        const stale = computeStaleState(lastUpdatedAt, staleThresholdMs);
        staleNode.style.display = stale.stale ? 'flex' : 'none';
        freshnessNode.textContent = stale.stale
            ? `Stale: ${formatAge(stale.ageMs)} old`
            : `Live update ${new Date(lastUpdatedAt || Date.now()).toLocaleTimeString('en-IN')}`;
    };

    const persist = () => {
        setSnapshot<PersistedSectorSnapshot>(snapshotKey, {
            sectors: lastSectors,
            breadthSeries: breadthSeries.toArray(),
            concentrationSeries: concentrationSeries.toArray(),
            lastUpdatedAt: lastUpdatedAt || Date.now(),
        });
    };

    const update = async () => {
        try {
            const indices = await fetchIndices();
            if (!indices.length) throw new Error('no sectors');

            const sectors: SectorView[] = [];
            for (const index of indices) {
                const info = SECTOR_MAP[index.name?.toUpperCase()] || SECTOR_MAP[index.symbol?.toUpperCase()];
                if (!info) continue;
                sectors.push({
                    name: info.name,
                    change: index.pChange || 0,
                    weight: info.weight,
                });
            }

            if (!sectors.length) throw new Error('no mapped sectors');

            breadthSeries.push(getBreadth(sectors));
            concentrationSeries.push(getConcentration(sectors));
            lastSectors = sectors;
            lastUpdatedAt = Date.now();
            persist();
            draw(sectors);
        } catch {
            if (lastSectors.length > 0) draw(lastSectors);
        }
    };

    const persisted = getSnapshot<PersistedSectorSnapshot>(snapshotKey);
    if (persisted?.sectors?.length) {
        lastSectors = persisted.sectors;
        persisted.breadthSeries?.forEach((v) => breadthSeries.push(v));
        persisted.concentrationSeries?.forEach((v) => concentrationSeries.push(v));
        lastUpdatedAt = persisted.lastUpdatedAt;
        draw(lastSectors);
    }

    const taskId = `phase2-sectors-${Math.random().toString(36).slice(2)}`;
    const unregister = chartRefreshScheduler.register(taskId, 'B', update, 3000);

    teardownMap.set(container, () => {
        unregister();
    });
}
